import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { Flex, Input, Modal, Text, TextVariant } from '@mprolab/uikit'
import { useTranslation } from 'react-i18next'
import { ethers } from 'ethers'
import { isResolved, resolveToken } from 'utils/swap/tokens'
import type { SwapToken } from 'utils/swap/types'

const RECENT_KEY = 'mprolab.swap.recent'
const RECENT_MAX = 5

/**
 * Recent picks, remembered as addresses only.
 *
 * Wrapped because storage is not always there: a private window, a browser
 * that blocks site data, a quota that is full. None of those are reasons for a
 * token list to fail to open.
 */
export function readRecent(): string[] {
  try {
    const raw = window.localStorage.getItem(RECENT_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter((v): v is string => typeof v === 'string').slice(0, RECENT_MAX)
  } catch {
    return []
  }
}

export function rememberRecent(address: string): void {
  try {
    const next = [address.toLowerCase(), ...readRecent().filter((a) => a !== address.toLowerCase())]
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(next.slice(0, RECENT_MAX)))
  } catch {
    // A preference we could not save is not worth a broken interaction.
  }
}

const List = styled(Flex)`
  flex-direction: column;
  gap: 4px;
  width: 420px;
  max-width: 100%;
  max-height: 50vh;
  overflow-y: auto;
  margin-top: 16px;

  ${({ theme }) => theme.mediaQueries.mobile} {
    width: 100%;
  }
`

const Row = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  /* 44px of height so a thumb can hit it. */
  min-height: 48px;
  padding: 10px 12px;
  border: none;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.gray10};
  color: ${({ theme }) => theme.colors.white};
  cursor: pointer;
  text-align: left;

  &:hover,
  &:focus-visible {
    background: ${({ theme }) => theme.colors.gray9};
  }
`

const Note = styled(Flex)`
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.gray10};
`

function shortAddress(address: string): string {
  return `${address.slice(0, 6)}…${address.slice(-4)}`
}

interface TokenListModalProps {
  tokens: SwapToken[]
  chainId: number
  mproAddress: string | null
  onSelect: (token: SwapToken) => void
  onDismiss?: () => void
}

/**
 * The token list, plus a way past it.
 *
 * The curated list is short by design — the tokens with real depth on Base —
 * but an aggregator that only trades a curated list is not an aggregator. So
 * an address pasted into the search box is resolved from chain and offered
 * with a plain warning that we do not vouch for it, rather than being refused.
 */
const TokenListModal: React.FC<TokenListModalProps> = ({
  tokens,
  chainId,
  mproAddress,
  onSelect,
  onDismiss
}) => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const [query, setQuery] = useState('')
  const [pasted, setPasted] = useState<SwapToken | null>(null)
  const [resolving, setResolving] = useState(false)

  const recent = useMemo(() => readRecent(), [])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    const known = [...tokens].sort((a, b) => {
      const ra = recent.indexOf(a.address.toLowerCase())
      const rb = recent.indexOf(b.address.toLowerCase())
      if (ra === rb) return 0
      if (ra === -1) return 1
      if (rb === -1) return -1
      return ra - rb
    })
    if (!q) return known
    return known.filter(
      (token) => token.symbol.toLowerCase().includes(q) || token.address.toLowerCase().includes(q)
    )
  }, [query, recent, tokens])

  useEffect(() => {
    const candidate = query.trim()
    if (!ethers.utils.isAddress(candidate)) {
      setPasted(null)
      return undefined
    }
    if (filtered.some((token) => token.address.toLowerCase() === candidate.toLowerCase())) {
      setPasted(null)
      return undefined
    }
    let cancelled = false
    setResolving(true)
    void resolveToken(candidate, chainId, mproAddress)
      .then((token) => {
        if (!cancelled) setPasted(isResolved(token) ? token : null)
      })
      .finally(() => {
        if (!cancelled) setResolving(false)
      })
    return () => {
      cancelled = true
    }
  }, [chainId, filtered, mproAddress, query])

  const choose = useCallback(
    (token: SwapToken) => {
      rememberRecent(token.address)
      onSelect(token)
      onDismiss?.()
    },
    [onDismiss, onSelect]
  )

  return (
    <Modal onDismiss={onDismiss} title={t('swap.selectToken')}>
      <Flex flexDirection="column" width="420px" style={{ maxWidth: '100%' }}>
        <Input
          value={query}
          name="tokenSearch"
          placeholder={t('swap.findTokens')}
          onChange={(e) => setQuery(e.target.value)}
        />
        <List>
          {filtered.map((token) => (
            <Row key={token.address} type="button" onClick={() => choose(token)}>
              <Text variant={TextVariant.BODY_SMALL_BOLD}>{token.symbol}</Text>
              <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                {token.isNative ? t('swap.nativeToken') : shortAddress(token.address)}
              </Text>
            </Row>
          ))}
          {filtered.length === 0 && !pasted && !resolving ? (
            <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
              {t('swap.noTokenFound')}
            </Text>
          ) : null}
        </List>
        {resolving ? (
          <Note>
            <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
              {t('swap.resolvingToken')}
            </Text>
          </Note>
        ) : null}
        {pasted ? (
          <>
            <Row type="button" onClick={() => choose(pasted)} style={{ marginTop: 12 }}>
              <Text variant={TextVariant.BODY_SMALL_BOLD}>
                {pasted.symbol} · {pasted.decimals} decimals
              </Text>
              <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                {shortAddress(pasted.address)}
              </Text>
            </Row>
            <Note>
              <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.yellow}>
                {t('swap.tokenNotOnOurList')}
              </Text>
            </Note>
          </>
        ) : null}
      </Flex>
    </Modal>
  )
}

export default TokenListModal
